/**
 * Steps: actions as the server records them (R-RNG-03). A step that consumed randomness carries
 * the PRNG state the server dealt for it, so anyone can replay a finished step while nobody can
 * foresee the next one.
 */
import { applyAction } from './apply.ts';
import { createGameWithEvents } from './create.ts';
import { RuleViolation } from './draft.ts';
import type { GameSetup } from './queries.ts';
import type { RngState } from './rng.ts';
import type { Action, ApplyResult, GameEvent, GameState } from './types.ts';

export interface GameStep {
  readonly action: Action;
  /** The PRNG state the action started from; absent when it used the state's own. */
  readonly rng?: RngState | undefined;
}

/**
 * Actions that involve no chance and no handover: they may be taken back freely. Everything
 * else — the wind, shots, the end of the turn — is a point of no return (docs/ux.md U-UNDO-01).
 */
export const UNDOABLE_ACTIONS: ReadonlySet<Action['type']> = new Set([
  'moveShip',
  'moveInPort',
  'enterPortRepair',
  'startFortRepair',
  'stopFortRepair',
]);

/** How many steps undo may take back: the undoable ones since the last point of no return. */
export function undoDepth(actions: readonly Action[]): number {
  let n = 0;
  for (let i = actions.length - 1; i >= 0; i--) {
    if (!UNDOABLE_ACTIONS.has((actions[i] as Action).type)) break;
    n++;
  }
  return n;
}

/** Applies one step: its action, from the step's PRNG state when it has one. */
export function applyStep(state: GameState, step: GameStep): ApplyResult {
  return applyAction(step.rng ? { ...state, rng: step.rng } : state, step.action);
}

const sameRng = (a: RngState, b: RngState) => a.every((v, i) => v === b[i]);

/**
 * Applies `action` from a freshly dealt PRNG state (R-RNG-03). The step keeps `fresh` only if
 * the action used it; otherwise the state keeps its own PRNG, exactly as without the deal.
 */
export function applyDealt(
  state: GameState,
  action: Action,
  fresh: RngState,
): { readonly result: ApplyResult; readonly step: GameStep } {
  const result = applyAction({ ...state, rng: fresh }, action);
  if (!result.ok || !sameRng(result.state.rng, fresh)) return { result, step: { action, rng: fresh } };
  return { result: { ...result, state: { ...result.state, rng: state.rng } }, step: { action } };
}

/**
 * Rebuilds a game from its setup and steps (R-RNG-02, R-RNG-03).
 * Throws `RuleViolation` if a step is illegal.
 */
export function replaySteps(
  setup: GameSetup,
  steps: readonly GameStep[],
): { state: GameState; events: GameEvent[] } {
  const created = createGameWithEvents(setup.map, setup.ruleset, setup.seed, setup.players, setup.options);
  let state = created.state;
  const events = [...created.events];
  for (const step of steps) {
    const result = applyStep(state, step);
    if (!result.ok) throw new RuleViolation(result.error.rule, result.error.reason, result.error.message);
    state = result.state;
    events.push(...result.events);
  }
  return { state, events };
}
